import { createContext, useContext, type ComponentType } from 'react';
import { useStore } from 'zustand';
import type { AgentGatewayService } from '@lib/services/servers/AgentGatewayService';
import type { EventBusService } from '@lib/services/eventBusService';
import type { IArtifactService } from '@lib/services/interfaces/artifactService';
import type { IStorageService } from '@lib/services/storageService';
import type { INotificationPort } from '@lib/interfaces/INotificationPort';
import type { AiChatState, AiChatStore } from './stateConfiguration';
import type { SessionStateManagerService } from './sessionStateManagerService';
import type { PartRendererMap, UiToolRegistry } from './ChatPane/parts';
import type { Interaction } from '@lib/models/Interaction';
import type { SendMessageOptions, UserMessageRendererProps } from './AiChatTypes';
import type { SendMessageResponse } from '@lib/models/SendMessageResponse';

export interface AiChatContextState {
    agentGatewayService: AgentGatewayService;
    eventBusService: EventBusService;
    artifactService: IArtifactService;
    storageService: IStorageService;
    notificationPort: INotificationPort;
    sessionStateManagerService: SessionStateManagerService;
    sessionStore: AiChatStore;
    showPromptHistory: boolean;
    showStatistics: boolean;
    showStickyHeader: boolean;
    showToolCalls: boolean;
    showModelSelector: boolean;
    showSessionInfo: boolean;
    showTaskList: boolean;
    showSubAgents: boolean;
    compactPrompt: boolean;
    showConnectionState: boolean;
    hidePromptInput: boolean;
    setShowToolCalls: (show: boolean) => void;
    friendlyMode: boolean;
    setFriendlyMode: (enabled: boolean) => void;
    currentModel: string | undefined;
    setCurrentModel: (modelId: string) => void;
    onVisibleQuestionChange?: (question: string | null) => void;
    onRenderPrompt?: (message: string) => { promptAsString?: string, json?: any };
    /**
     * Per-part renderer overrides. The dispatcher in `MessagePart` looks
     * up `partRenderers[kind]` first and falls back to the built-in
     * `DEFAULT_PART_RENDERERS[kind]` when an override isn't provided.
     * Hosts that want to swap a single kind (say, customize how
     * function calls render) provide a partial map; everything else
     * keeps the built-in.
     */
    partRenderers?: PartRendererMap;
    /**
     * UI-tool registry — maps a tool name to a React renderer or a fire-once
     * action. The built-in `uiTool` dispatcher (`DEFAULT_PART_RENDERERS.uiTool`)
     * resolves it; build one with `createUiToolRegistry()` and pass it as the
     * `uiTools` prop. Unregistered UI tools fall back to a plain tool-call render.
     */
    uiTools?: UiToolRegistry;
    /** Show a persona header row above each message (see AiChatProps.showPersona). */
    showPersona?: boolean;
    /** Formats a message's timestamp for the persona row. */
    formatTimestamp?: (date: Date) => string;
    /** Resolves the persona label + initials for a message. */
    resolvePersona?: (args: { isUser: boolean; message: Interaction }) => { label: string; initials?: string, picture?: string };
    /** Custom renderer for the user-message body (the persona row is still shown above it). */
    userMessageRenderer?: ComponentType<UserMessageRendererProps>;

    sendMessage: (text: string, options?: SendMessageOptions) => Promise<SendMessageResponse>;
}

export const AiChatServiceContext = createContext<AiChatContextState | undefined>(undefined);

export const useAiChatServices = (): AiChatContextState => {
    const context = useContext(AiChatServiceContext);
    if (context === undefined) {
        throw new Error('useAiChatServices must be used within an AiChat component');
    }
    return context;
};

export function useAiChatSessionStore<T>(selector: (state: AiChatState) => T): T {
    const { sessionStore } = useAiChatServices();
    return useStore(sessionStore, selector);
}
